import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

import { useMasterMemory } from '@/hooks/master/useMasterMemory'

type MasterPanelProps = {
  enabled?: boolean
}

export function MasterMemoryPanel({ enabled = true }: MasterPanelProps) {
  const { entries, loading, refresh, available } = useMasterMemory({ enabled })
  const total = Object.values(entries).reduce((acc, items) => acc + items.length, 0)
  const isUnavailable = enabled && !available

  return (
    <Card className="border border-border/70 bg-background/70">
      <CardHeader className="flex items-center justify-between gap-2">
        <CardTitle className="text-lg">Memory browser</CardTitle>
        <Badge variant="outline" className="text-xs">
          {total} entries
        </Badge>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {isUnavailable ? (
          <p className="text-xs text-muted-foreground">Master insights are unavailable in this environment.</p>
        ) : loading && total === 0 ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : total === 0 ? (
          <p className="text-xs text-muted-foreground">No memory episodes yet.</p>
        ) : (
          Object.entries(entries).map(([agent, list]) => (
            <div key={agent} className="space-y-1 rounded-xl border border-border/80 bg-background/40 p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-foreground">{agent}</span>
                <Badge variant="outline" className="text-[11px]">
                  {list.length} entries
                </Badge>
              </div>
              <ul className="space-y-1 text-[11px] text-muted-foreground">
                {list.slice(0, 4).map((entry) => (
                  <li key={entry.id ?? entry.timestamp ?? Math.random()}>
                    {entry.summary ?? entry.type} ·{' '}
                    <span className="text-xs">{new Date(entry.timestamp ?? Date.now()).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
        <button
          type="button"
          className="text-xs uppercase tracking-wide text-primary-foreground dark:text-amber-200 hover:underline disabled:opacity-50"
          onClick={() => refresh?.()}
          disabled={!available}
        >
          Refresh
        </button>
      </CardContent>
    </Card>
  )
}
